import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface LegalNoticeProps {
  onBack: () => void;
}

export default function LegalNotice({ onBack }: LegalNoticeProps) {
  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-purple-100 selection:text-purple-900 relative overflow-hidden font-sans">
      {/* Colorful Gradient Backdrop */}
      <div className="absolute inset-x-0 top-0 h-[600px] pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-15%] w-[55%] h-[60%] rounded-full bg-purple-200/35 blur-[120px]" />
        <div className="absolute top-[-5%] right-[-10%] w-[50%] h-[55%] rounded-full bg-orange-100/40 blur-[110px]" />
        <div className="absolute top-[20%] left-[25%] w-[45%] h-[50%] rounded-full bg-blue-100/30 blur-[130px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-20">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="group flex items-center gap-2 text-sm font-semibold text-zinc-550 hover:text-zinc-950 transition-colors mb-12 cursor-pointer"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        {/* Article Container */}
        <article className="space-y-10">
          <header className="space-y-4 border-b border-zinc-100 pb-8">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700 uppercase">
              BETA Disclaimer
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-950">
              Legal Notice
            </h1>
            <p className="text-sm font-semibold text-zinc-400">
              Last Updated: June 26, 2026
            </p>
          </header>

          <section className="space-y-6 text-[15px] leading-relaxed text-zinc-650 font-medium">
            <p>
              This Legal Notice governs your access to and usage of the <strong>Design Studio</strong> visual platform.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">1. Copyright and Company Attribution</h2>
            <p>
              The Design Studio website, editor suite, layout assets, diagrams, and codebase are properties developed, owned, and maintained by <strong>Caselgrid</strong>. All proprietary rights, logos, trade secrets, and patents associated with the platform are reserved by Caselgrid.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">2. Beta Software Environment</h2>
            <p>
              Design Studio is provided on an "as-is" and "as-available" basis under an active <strong>Beta release stage</strong>. This means the codebase is under continuous modification. Caselgrid does not represent or warrant that the application will be uninterrupted, error-free, secure, or free from data corruption or loss.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">3. Full Assumption of Risk and Liability Waiver</h2>
            <p className="bg-red-50/50 border border-red-100 rounded-2xl p-5 text-red-950 text-sm">
              <strong>MANDATORY LEGAL WARNING:</strong> The use of Design Studio is conducted <strong>entirely at the user's own risk</strong>. In no event shall Caselgrid, its creators, developers, contributors, or corporate affiliates be held liable for any legal issues, claims, damages, liabilities, losses, costs, or expenses. The user agrees to fully release, indemnify, and hold harmless the developers and Caselgrid from any legal actions or consequences stemming from the use of the platform.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">4. Contact Information</h2>
            <p>
              For legal inquiries, brand permissions, or corporate compliance questions, please contact Caselgrid directly through official channels.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
